import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';
import { isGulpOrBranching, getCdnUrl, getBackupCdnUrl, getResourcePath } from 'owa-config';
import { logStartGreyError } from 'owa-analytics-start';
import { isBootFeatureEnabled } from 'owa-metatags';
import { trace, type TraceErrorObject } from 'owa-trace';

let owaTrustedTypesPolicy: Pick<TrustedTypePolicy, 'createHTML' | 'createScriptURL'> | undefined;
// Regex to match either /mail/sw.js or /mail/<any string>/sw.js or /mail/<any string>/<any string>/sw.js
const regex: RegExp = /^\/mail\/([^\/]+\/)*sw\.js/;
// Regex to match either /<any string>/<any string>/sw_webpush.js or /sw_<any string>_webpush.js?branch=<any string>
// Includes OWA mini `{prefix}/sw_mini_webpush.js` as defined by router in Microsoft.Owa.Web.Server/Startup.cs
const webpushRegex: RegExp =
    /^\/([^\/]+\/)*sw(_[^\/_]+)*_webpush\.js(\?(branch|ring|version)=[^\/_=]+){0,1}$/;
// List of external sources in callstacks
const externalSourceList: RegExp[] = [
    // /<anonymous>/ alone could match an anonymous callback to one of the Array methods (eg., Array.forEach())
    // However, /<anonymous>:\d+/ should only match extension code
    /<anonymous>:\d+/,
    /-extension:\/\//,
];

function markErrorAsExternal(error: TraceErrorObject): void {
    const { stack } = error;
    if (stack) {
        for (const source of externalSourceList) {
            // Mark errors as external from external sources like extensions, anonymous functions, etc.
            if (source.test(stack)) {
                error.external = true;
                break;
            }
        }
    }
}

export const defaultCreateHTML = (
    html: string,
    type: string,
    sink: string,
    isTrustedTypesEnabled: boolean
): string => {
    if (html === '') {
        return '';
    }

    const error: TraceErrorObject = new Error("This document requires 'TrustedHTML' assignment.");
    markErrorAsExternal(error);

    trace.warn(`${error.message}: ${error.stack}`);

    logStartGreyError('TrustedTypesHTMLDefaultPolicyError', error, {
        type,
        sink,
        isHTMLSanitized: isTrustedTypesEnabled,
    });

    if (isTrustedTypesEnabled) {
        // If the HTML is untrusted, we sanitize it by only allowing safe HTMLs elements
        return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    }
    return html;
};

export const defaultCreateScriptUrl = (
    url: string,
    type: string,
    sink: string,
    isTrustedTypesEnabled: boolean
): string => {
    const cdnUrl = `https:${getCdnUrl()}`;
    const backupCdnUrl = `https:${getBackupCdnUrl()}`;
    const currentDomain = window.location.origin;
    const blobUrl = `blob:${currentDomain}`;

    if (
        url.startsWith(currentDomain) ||
        url.startsWith(cdnUrl) ||
        url.startsWith(backupCdnUrl) ||
        url.startsWith(blobUrl) ||
        // DoubleClick impression tracking endpoint which is generated and needed by ads
        url.startsWith('https://ad.doubleclick.net/ddm/trackimpj') ||
        // Some 1JS files are always calling the WW cdns regarless of the environment, so we need to allow them here while we fix it
        // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/352929
        url.startsWith('https://res.cdn.office.net') ||
        url.startsWith('https://res-1.cdn.office.net')
    ) {
        return url;
    }

    const error = new Error("This document requires 'TrustedScriptURL' assignment.");

    trace.warn(`${error.message}: ${error.stack}`);

    logStartGreyError('TrustedTypesScriptURLDefaultPolicyError', error, {
        url,
        type,
        sink,
        isScriptURLBlocked: isTrustedTypesEnabled,
    });

    if (isTrustedTypesEnabled) {
        // If the script is untrusted, we throw an error to block the script execution
        throw error;
    }
    return url;
};

export const defaultCreateScript = (
    script: string,
    type: string,
    sink: string,
    isTrustedTypesEnabled: boolean
): string => {
    const error = new Error("This document requires 'TrustedScript' assignment.");
    markErrorAsExternal(error);

    trace.warn(`${error.message}: ${error.stack}`);

    logStartGreyError('TrustedTypesScriptDefaultPolicyError', error, {
        script,
        type,
        sink,
        isScriptBlocked: isTrustedTypesEnabled,
    });

    if (isTrustedTypesEnabled) {
        // If the script URL is untrusted, we throw an error to block the script execution
        throw error;
    }
    return script;
};

const isValidUrl = (url: string): boolean => {
    if (url === `${getResourcePath()}mos-proxy.js`) {
        return true;
    }
    return false;
};

if (window.trustedTypes) {
    owaTrustedTypesPolicy = window.trustedTypes.createPolicy('owaTrustedTypesPolicy', {
        createHTML: (html: string, extendedConfig: Config): string => {
            if (html === '') {
                return '';
            }
            const trustedHTML = DOMPurify.sanitize(html, {
                ...extendedConfig,
                RETURN_TRUSTED_TYPE: false,
            });

            return trustedHTML?.toString() ?? '';
        },
        createScriptURL: (url: string): string => {
            if (isValidUrl(url) || regex.test(url) || webpushRegex.test(url)) {
                return url;
            }

            // The URL is not meeting the conditions for a safe URL so return empty string
            return '';
        },
    });

    if (isBootFeatureEnabled('fwk-warn-duplicate-default-tt-policy')) {
        // This is to check if a default policy was already created by an external source before us
        const isDefaultPolicyCreatedByExternalSourceFirst = !!window.trustedTypes.defaultPolicy;

        // Save a reference to the original createPolicy method
        const originalCreatePolicy = window.trustedTypes.createPolicy;

        // Override the createPolicy method with proper TypeScript typing
        window.trustedTypes.createPolicy = function <Options extends TrustedTypePolicyOptions>(
            policyName: string,
            policyOptions?: Options
        ) {
            // Check if this is an attempt to create a default policy when one already exists
            if (
                policyName === 'default' &&
                window.trustedTypes &&
                window.trustedTypes.defaultPolicy
            ) {
                const error: TraceErrorObject = new Error(
                    'Duplicated Default Trusted Types policy'
                );
                trace.warn(`${error.message}: ${error.stack}`);

                if (isDefaultPolicyCreatedByExternalSourceFirst) {
                    // We should throw the error to avoid using a pre-existing default policy from an unknown source.
                    logStartGreyError('ExternalDefaultTrustedTypesPolicyCreatedFirst', error);
                    throw error;
                }

                // There is no need to throw the error if we created the default policy first, it can't be overwritten by users or extensions.
                logStartGreyError('OwaDefaultTrustedTypesPolicyCreatedFirst', error);

                // Return the existing default policy
                return window.trustedTypes.defaultPolicy;
            }

            // For all other cases, use the original implementation
            return originalCreatePolicy.call(window.trustedTypes, policyName, policyOptions);
        };
    }

    /**
     * The Trusted Types default policy will only be define on prod builds, on dev mode and PR branches we want to
     * keep throwing the errors from the CSP so that we fix them before merging to master.
     * */
    if (!isGulpOrBranching() && isBootFeatureEnabled('fwk-enable-default-trusted-types-policy')) {
        const isTrustedTypesEnabled = isBootFeatureEnabled('fwk-enforce-trusted-types');

        /**
         * When there is no default trusted types policy defined, execution of code related to an untrusted input will be blocked
         * by the CSP when it is in enforce mode and an error will be thrown, but when there is a default trusted types policy
         * defined, since it catches all unhandled trusted type errors, code execution will not be blocked by CSP even when it
         * is in enforce mode and the error will not be thrown, so to keep the same behavior and block/sanitize code related to untrusted
         * inputs we have to handle each case manually on these functions.
         */
        window.trustedTypes.createPolicy('default', {
            createHTML: (html: string, type: string, sink: string) =>
                defaultCreateHTML(html, type, sink, isTrustedTypesEnabled),
            createScriptURL: (url: string, type: string, sink: string) =>
                defaultCreateScriptUrl(url, type, sink, isTrustedTypesEnabled),
            createScript: (script: string, type: string, sink: string) =>
                defaultCreateScript(script, type, sink, isTrustedTypesEnabled),
        });
    }
}

export { owaTrustedTypesPolicy, regex, webpushRegex };
